import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormControlLabel,
  Radio,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  // CardMedia,
  CardContent,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from "@mui/material";
import {
  Telegram as TelegramIcon,
  TravelExplore as TravelExploreIcon,
  FindInPage as FindInPageIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Difference as DifferenceIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import Grid from "@mui/material/GridLegacy";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { articleApi, authorApi } from "../../services/api";
import EditorJSComponent from "../../components/editor/EditorJS";
import type { Article, Author } from "../../models";
import MediaSelector from "../../components/media/MediaSelector";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CKRichEditor from "../../components/editor/CKRichEditor";
import SeoEditor from "../../components/cms/SeoEditor";
import MarkdownEditor from "../../components/cms/MarkdownEditor";
import MarkdownDiffViewer from "../../components/cms/MarkdownDiffViewer";
import DelphiAiAssistant from "../../components/cms/DelphiAiAssistant";
import type { OutputData } from "@editorjs/editorjs";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`article-tabpanel-${index}`}
      aria-labelledby={`article-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

type ContentType = "blocks" | "plain" | "html" | "markdown";

const ArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewArticle = !id;
  const isViewMode = location.pathname.includes("/view");
  const [article, setArticle] = useState<Partial<Article>>({
    title: "",
    article_type: "post",
    published: false,
    content_blocks: [],
    content_html: "",
    content_markdown: "",
    content_text: "",
    seo: {
      meta_title: "",
      meta_description: "",
      meta_image: "",
      meta_robots: "",
      meta_viewport: "",
      canonical_url: "",
      keywords: "",
      structured_data: {},
      meta_social: [],
    },
  });
  const [contentType, setContentType] = useState<ContentType>("blocks");
  const [loading, setLoading] = useState(!isNewArticle);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [checkingGoogleIndex, setCheckingGoogleIndex] = useState(false);
  const [submittingIndexNow, setSubmittingIndexNow] = useState(false);
  const [sendingTestTelegram, setSendingTestTelegram] = useState(false);
  const [scheduledPublishDate, setScheduledPublishDate] = useState<Date | null>(
    null
  );
  const [editorData, setEditorData] = useState<OutputData>({ blocks: [] });
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [selectedAuthor, setSelectedAuthor] = useState<Author | null>(null);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [originalArticle, setOriginalArticle] = useState<Article | null>(null);
  const [draftArticle, setDraftArticle] = useState<Article | null>(null);
  const [viewVersion, setViewVersion] = useState<"live" | "draft" | "diff">("live");

  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<'en' | 'hi'>('en');
  const [delphiAssistantOpen, setDelphiAssistantOpen] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // One model call writes the summary in English and Hindi from the (possibly unsaved) body; the editor reviews it and saves.
  const handleGenerateSummary = async () => {
    if (!article?.id) return;
    try {
      setGeneratingSummary(true);
      const res = await articleApi.generateSummary(article.id, {
        content_markdown: getDraftMarkdown('en'),
        title: article.title_en || article.title,
      });
      setArticle((prev) => ({
        ...prev,
        summary: res.data.summary_en,
        summary_en: res.data.summary_en,
        ...(res.data.summary_hi ? { summary_hi: res.data.summary_hi } : {}),
      }));
      setNotification({ open: true, message: "Summary generated in English and Hindi. Review it and save the draft.", severity: "success" });
    } catch (err: any) {
      setNotification({ open: true, message: err.response?.data?.detail || "Could not generate the summary", severity: "error" });
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleApplyDelphiContent = (newMarkdown: string, newTitle?: string, newDescription?: string, newSummary?: string) => {
    setMarkdownContent(newMarkdown);
    setArticle((prev) => ({
      ...prev,
      content_markdown: newMarkdown,
      content_markdown_en: newMarkdown,
      ...(newTitle ? { title: newTitle, title_en: newTitle } : {}),
      ...(newDescription ? { description: newDescription, description_en: newDescription } : {}),
      ...(newSummary ? { summary: newSummary, summary_en: newSummary } : {}),
    }));
    setNotification({
      open: true,
      message: "Refined English content applied to draft! Remember to save your draft.",
      severity: "success",
    });
  };

  const handleApplyDelphiHindi = (data: { title_hi?: string; description_hi?: string; summary_hi?: string; content_markdown_hi?: string }) => {
    setArticle((prev) => ({
      ...prev,
      ...(data.title_hi ? { title_hi: data.title_hi } : {}),
      ...(data.description_hi ? { description_hi: data.description_hi } : {}),
      ...(data.summary_hi ? { summary_hi: data.summary_hi } : {}),
      ...(data.content_markdown_hi ? { content_markdown_hi: data.content_markdown_hi } : {}),
    }));
    if (currentLang === 'hi' && data.content_markdown_hi) {
      setMarkdownContent(data.content_markdown_hi);
    }
    setNotification({
      open: true,
      message: "Hindi translation applied to draft! Switch language to Hindi or save draft to keep changes.",
      severity: "success",
    });
  };

  const handleApplyDelphiImage = (imageUrl: string, imageId?: number) => {
    setArticle((prev) => ({
      ...prev,
      legacy_feature_image_url: imageUrl,
      ...(imageId ? { feature_image: imageId } : {}),
    }));
    setNotification({
      open: true,
      message: "Generated feature image applied to draft!",
      severity: "success",
    });
  };

  const getBaselineMarkdown = (lang: 'en' | 'hi' = currentLang): string => {
    if (!originalArticle) return "";
    const key = `content_markdown_${lang}` as keyof Article;
    // @ts-ignore
    return (originalArticle[key] as string) || originalArticle.content_markdown || "";
  };

  const getDraftMarkdown = (lang: 'en' | 'hi' = currentLang): string => {
    const target = draftArticle || article;
    if (!target) return markdownContent;
    const key = `content_markdown_${lang}` as keyof Article;
    // @ts-ignore
    return (target[key] as string) || target.content_markdown || markdownContent;
  };

  const getLocalizedValue = (field: keyof Article, lang: 'en' | 'hi' = currentLang) => {
    const key = `${field}_${lang}` as keyof Article;
    // @ts-ignore
    return article?.[key] !== undefined ? article?.[key] : article?.[field];
  };

  const updateLocalizedValue = (field: keyof Article, value: any) => {
    const key = `${field}_${currentLang}` as keyof Article;
    setArticle((prev) => ({ ...prev, [key]: value }));
  };

  const handleLanguageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLang: 'en' | 'hi' | null,
  ) => {
    if (newLang !== null && newLang !== currentLang) {
      // Allow switching language
      // If we are in blocks mode, sync the current editor data to the current language field
      if (contentType === 'blocks') {
        const currentBlocksKey = `content_blocks_${currentLang}` as keyof Article;
        setArticle(prev => ({
          ...prev,
          [currentBlocksKey]: editorData.blocks
        }));

        // Load the new language data
        const newBlocksKey = `content_blocks_${newLang}` as keyof Article;
        // @ts-ignore
        const newBlocks = article?.[newBlocksKey] || [];
        setEditorData({ blocks: newBlocks });
      }
      // If we are in markdown mode, sync the current markdown content to the current language field
      if (contentType === 'markdown') {
        const currentMarkdownKey = `content_markdown_${currentLang}` as keyof Article;
        setArticle(prev => ({
          ...prev,
          [currentMarkdownKey]: markdownContent
        }));

        // Load the new language data
        const newMarkdownKey = `content_markdown_${newLang}` as keyof Article;
        // @ts-ignore
        const newMarkdown = article?.[newMarkdownKey] || "";
        setMarkdownContent(newMarkdown);
      }
      setCurrentLang(newLang);
    }
  };

  // Fetch authors list on component mount
  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        setLoadingAuthors(true);
        const response = await authorApi.list({ page_size: 100 });
        const fetchedAuthors = response.data.results || [];
        setAuthors(fetchedAuthors);
        
        // If we have a selected author, try to find the full details from the list
        if (selectedAuthor && fetchedAuthors.length > 0) {
          const foundAuthor = fetchedAuthors.find(a => a.id === selectedAuthor.id);
          if (foundAuthor) {
            setSelectedAuthor(foundAuthor);
          }
        }
      } catch (error) {
        console.error("Error fetching authors:", error);
        setAuthors([]);
      } finally {
        setLoadingAuthors(false);
      }
    };

    fetchAuthors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const articleResponse = await articleApi.getById(
          parseInt(id || "0", 10)
        );
        const fetchedArticle = articleResponse.data;
        let workingArticle = fetchedArticle;
        let orig: Article | null = null;
        let draft: Article | null = null;

        if (fetchedArticle.is_draft) {
          draft = fetchedArticle;
          setDraftArticle(draft);
          // If this is a draft of a published article, fetch the published original for comparison
          if (fetchedArticle.draft_of) {
            try {
              const origRes = await articleApi.getById(fetchedArticle.draft_of);
              orig = origRes.data;
              setOriginalArticle(orig);
            } catch (e) {
              console.error("Error fetching original article for draft:", e);
            }
          }
          setArticle(draft);
          if (isViewMode && orig) {
            setViewVersion("diff"); // Automatically default to diff in view mode!
          }
        } else {
          orig = fetchedArticle;
          setOriginalArticle(orig);

          // Check if article has an existing draft
          if (fetchedArticle.has_draft && fetchedArticle.latest_draft?.id) {
            try {
              const draftRes = await articleApi.getById(fetchedArticle.latest_draft.id);
              draft = draftRes.data;
              setDraftArticle(draft);
              if (isViewMode) {
                setViewVersion("diff"); // Default to diff so user immediately sees what changed!
              }
            } catch (e) {
              console.error("Error fetching latest draft:", e);
            }
          }

          // If in edit mode and not a draft, create or fetch draft
          if (!isViewMode) {
            try {
              const draftResponse = await articleApi.createDraft(fetchedArticle.id!);
              workingArticle = draftResponse.data;
              draft = workingArticle;
              setDraftArticle(draft);
              setArticle(workingArticle);
            } catch (error) {
              console.error("Error handling draft:", error);
              setNotification({
                open: true,
                message: "Failed to handle draft",
                severity: "error",
              });
              setArticle(fetchedArticle);
            }
          } else {
            setArticle(fetchedArticle);
          }
        }

        const activeForContentType = workingArticle || fetchedArticle;
        if (activeForContentType.content_type) {
          setContentType(activeForContentType.content_type);
        } else {
          // Determine content type based on available content
          if ((activeForContentType.content_blocks?.length || 0) > 0 || (activeForContentType.content_blocks_en?.length || 0) > 0) {
            setContentType("blocks");
            setEditorData({ blocks: activeForContentType.content_blocks_en || activeForContentType.content_blocks || [] });
          } else if (activeForContentType.content_html) {
            setContentType("html");
          } else if (activeForContentType.content_markdown) {
            setContentType("markdown");
          } else if (activeForContentType.content_text) {
            setContentType("plain");
          }
        }

        // Initialize markdown content for preview/editing
        const markdownKey = `content_markdown_${currentLang}` as keyof Article;
        // @ts-ignore
        const localizedMarkdown = activeForContentType?.[markdownKey] || activeForContentType?.content_markdown || "";
        setMarkdownContent(localizedMarkdown);

        // Load author if available - will be matched with authors list when it loads
        if (activeForContentType.author && activeForContentType.author_name) {
          setSelectedAuthor({
            id: activeForContentType.author,
            username: '',
            name: activeForContentType.author_name,
          });
        } else {
          setSelectedAuthor(null);
        }
      } catch (error) {
        console.error("Error fetching article:", error);
        setNotification({
          open: true,
          message: "Failed to fetch article",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchArticle();
    }
  }, [id, isViewMode]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditorDataChange = (newData: OutputData) => {
    setEditorData(newData);
    // setArticle(prev => ({
    //   ...prev,
    //   content_blocks: newData.blocks
    // }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const articleToSave = {
        ...article,
        // Update the current language's content blocks from editor state
        [`content_blocks_${currentLang}`]: editorData.blocks,
        // Also update the main content_blocks field for backward compatibility/default behavior
        content_blocks: currentLang === 'en' ? editorData.blocks : article.content_blocks,
        // Update the current language's markdown content from local state
        [`content_markdown_${currentLang}`]: markdownContent,
        // Also update the main content_markdown field for backward compatibility/default behavior
        content_markdown: currentLang === 'en' ? markdownContent : article.content_markdown,
        content_html: article.content_html,
        content_text: article.content_text,
        content_type: contentType,
      };

      if (isNewArticle) {
        await articleApi.create(articleToSave);
        setNotification({
          open: true,
          message: "Article created successfully",
          severity: "success",
        });
      } else {
        await articleApi.update(article.id!, articleToSave);
        setNotification({
          open: true,
          message: "Article saved successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error saving article:", error);
      setNotification({
        open: true,
        message: `Failed to ${isNewArticle ? "create" : "save"} article`,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await articleApi.publish(article.id!);
      setNotification({
        open: true,
        message: "Article published successfully",
        severity: "success",
      });
      // Navigate back to article list
      navigate("/cms/articles");
    } catch (error) {
      console.error("Error publishing article:", error);
      setNotification({
        open: true,
        message: "Failed to publish article",
        severity: "error",
      });
    }
  };

  const handleSchedulePublish = async () => {
    try {
      if (!article?.id) return;

      const scheduledPublishAt = article.scheduled_publish_at;
      const autoPublishEnabled = article.auto_publish_enabled || false;

      if (autoPublishEnabled && !scheduledPublishAt) {
        setNotification({
          open: true,
          message: "Please set a scheduled publish time",
          severity: "error",
        });
        return;
      }

      await articleApi.schedulePublish(article.id, {
        scheduled_publish_at: scheduledPublishAt || null,
        auto_publish_enabled: autoPublishEnabled,
      });

      setNotification({
        open: true,
        message: "Publishing schedule saved successfully",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Error scheduling publish:", error);
      setNotification({
        open: true,
        message: error.response?.data?.error || "Failed to schedule publishing",
        severity: "error",
      });
    }
  };

  const handleSaveSocialSharing = async () => {
    try {
      if (!article?.id) return;

      const response = await articleApi.setupSocialSharing(article.id, {
        social_sharing: article.social_sharing || {},
      });

      if (response.data) {
        setArticle((prev) => ({
          ...prev,
          social_sharing: response.data.social_sharing,
        }));
      }

      setNotification({
        open: true,
        message: "Social sharing settings saved successfully",
        severity: "success",
      });
    } catch (error: any) {
      console.error("Error saving social sharing settings:", error);
      setNotification({
        open: true,
        message: error.response?.data?.error || "Failed to save social sharing settings",
        severity: "error",
      });
    }
  };

  const handleShareNow = async (platform: string) => {
    try {
      if (!article?.id) return;

      const response = await articleApi.shareOnSocial(article.id, {
        platforms: [platform],
      });

      setNotification({
        open: true,
        message: response.data?.message || `Shared on ${platform} successfully`,
        severity: "success",
      });

      // Update the article with the new social sharing status
      if (response.data?.article) {
        setArticle(response.data.article);
      }
    } catch (error) {
      console.error(`Error sharing on ${platform}:`, error);
      setNotification({
        open: true,
        message: `Failed to share on ${platform}`,
        severity: "error",
      });
    }
  };

  const handleSendTelegram = async () => {
    if (!article?.id) return;
    try {
      const res = await articleApi.sendToTelegram(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "Article sent to Telegram channel successfully!",
        severity: "success",
      });
      if (res.data?.article) {
        setArticle(res.data.article);
      }
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to send article to Telegram channel",
        severity: "error",
      });
    }
  };

  const handleSendTestTelegram = async () => {
    if (!article?.id) return;
    setSendingTestTelegram(true);
    try {
      const res = await articleApi.sendToTestTelegram(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "Test message sent to Telegram channel (@test_ai11news) successfully!",
        severity: "success",
      });
      if (res.data?.article) {
        setArticle(res.data.article);
      }
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to send test message to Telegram channel",
        severity: "error",
      });
    } finally {
      setSendingTestTelegram(false);
    }
  };

  const handleRequestIndexing = async () => {
    if (!article?.id) return;
    try {
      const res = await articleApi.requestIndexing(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "Google Indexing requested successfully!",
        severity: "success",
      });
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to request Google Indexing",
        severity: "error",
      });
    }
  };

  const handleRequestIndexNow = async () => {
    if (!article?.id) return;
    setSubmittingIndexNow(true);
    try {
      const res = await articleApi.requestIndexNow(article.id);
      setNotification({
        open: true,
        message: res.data?.message || "IndexNow (Bing/Yandex) requested successfully!",
        severity: "success",
      });
      setArticle((prev) => ({
        ...prev,
        indexnow_submitted_at: new Date().toISOString(),
      }));
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to submit to IndexNow",
        severity: "error",
      });
    } finally {
      setSubmittingIndexNow(false);
    }
  };

  const handleCheckGoogleIndex = async () => {
    if (!article?.id) return;
    setCheckingGoogleIndex(true);
    try {
      const res = await articleApi.checkGoogleIndex(article.id);
      if (res.data?.google_index_status) {
        setArticle((prev) => ({
          ...prev,
          google_index_status: res.data.google_index_status,
        }));
      }
      setNotification({
        open: true,
        message: res.data?.message || "Google Index status checked successfully!",
        severity: "success",
      });
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.response?.data?.error || "Failed to check Google Index status",
        severity: "error",
      });
    } finally {
      setCheckingGoogleIndex(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleImageSelect = (field: "feature_image", value: number) => {
    setArticle((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUrlSelect = (
    field: "legacy_feature_image_url",
    value: string
  ) => {
    setArticle((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isNewArticle && !article) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Article not found
        </Typography>
        <Button
          component={Link}
          to="/cms/articles"
          variant="contained"
          sx={{ mt: 2 }}
        >
          Back to Articles
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/cms/articles" underline="hover">
          Articles
        </MuiLink>
        <Typography color="text.primary">
          {isNewArticle
            ? "New Article"
            : article?.title || `Article #${article?.id}`}
        </Typography>
      </Breadcrumbs>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backdropFilter: "blur(12px)",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(29, 31, 43, 0.88)"
              : "rgba(255, 255, 255, 0.88)",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.4)"
              : "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={article?.published ? "Published (Live)" : "Draft"}
              color={article?.published ? "success" : "default"}
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {article?.has_draft && !article?.is_draft && (
              <Chip
                label="Pending Draft Exists"
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
            {article?.is_draft && (
              <Chip
                label={`Draft of #${article.draft_of || ""}`}
                color="info"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
          {isViewMode ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/cms/articles/${id}/edit`)}
              >
                Edit Article
              </Button>
              <ToggleButtonGroup
                value={currentLang}
                exclusive
                onChange={handleLanguageChange}
                aria-label="language"
                size="small"
                sx={{ ml: 1 }}
              >
                <ToggleButton value="en">English</ToggleButton>
                <ToggleButton value="hi">Hindi</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setDelphiAssistantOpen(true)}
                sx={{
                  ml: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(168,85,247,0.15) 100%)',
                  borderColor: 'rgba(168,85,247,0.5)',
                  '&:hover': {
                    borderColor: 'rgba(168,85,247,0.9)',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(168,85,247,0.25) 100%)',
                  },
                }}
              >
                Delphi AI Assistant
              </Button>
              {article?.published && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setUnpublishDialogOpen(true)}
                  sx={{ ml: 1 }}
                >
                  Unpublish
                </Button>
              )}
              {!article?.published && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handlePublish}
                  sx={{ ml: 1 }}
                >
                  Publish
                </Button>
              )}
              <Dialog
                open={unpublishDialogOpen}
                onClose={() => setUnpublishDialogOpen(false)}
              >
                <DialogTitle>Unpublish Article</DialogTitle>
                <DialogContent>
                  Are you sure you want to unpublish this article? It will no
                  longer be visible to the public.
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setUnpublishDialogOpen(false)}
                    color="inherit"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      setUnpublishDialogOpen(false);
                      try {
                        await articleApi.unpublish(article.id!);
                        setNotification({
                          open: true,
                          message: "Article unpublished successfully",
                          severity: "success",
                        });
                        // Optionally, refresh the article data
                        const response = await articleApi.getById(article.id!);
                        setArticle(response.data);
                      } catch (error) {
                        setNotification({
                          open: true,
                          message: "Failed to unpublish article",
                          severity: "error",
                        });
                      }
                    }}
                    color="warning"
                  >
                    Unpublish
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave()}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={24} />
                ) : isNewArticle ? (
                  "Create Article"
                ) : (
                  "Save Draft"
                )}
              </Button>

              <ToggleButtonGroup
                value={currentLang}
                exclusive
                onChange={handleLanguageChange}
                aria-label="language"
                size="small"
                sx={{ ml: 1 }}
              >
                <ToggleButton value="en">English</ToggleButton>
                <ToggleButton value="hi">Hindi</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setDelphiAssistantOpen(true)}
                sx={{
                  ml: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(168,85,247,0.15) 100%)',
                  borderColor: 'rgba(168,85,247,0.5)',
                  '&:hover': {
                    borderColor: 'rgba(168,85,247,0.9)',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(168,85,247,0.25) 100%)',
                  },
                }}
              >
                Delphi AI Assistant
              </Button>
              {!isNewArticle && article?.is_draft && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={async () => {
                    if (
                      window.confirm(
                        "Are you sure you want to discard this draft? This action cannot be undone."
                      )
                    ) {
                      try {
                        await articleApi.discardDraft(article.id!);
                        navigate("/cms/articles");
                      } catch (err) {
                        setNotification({
                          open: true,
                          message: "Failed to discard draft",
                          severity: "error",
                        });
                      }
                    }
                  }}
                >
                  Discard Draft
                </Button>
              )}
              {!isNewArticle && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => {
                    window.open(
                      `${import.meta.env.VITE_APP_URL}/blogs/${article.uuid}`,
                      "_blank"
                    );
                  }}
                >
                  Preview
                </Button>
              )}
              {!isNewArticle && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handlePublish}
                >
                  Publish
                </Button>
              )}
            </>
          )}
          </Stack>
        </Stack>
      </Paper>

      {/* Draft Notification Alert Banners */}
      {article?.has_draft && !article?.is_draft && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2,
            borderWidth: 1.5,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(237, 108, 2, 0.12)"
                : "rgba(237, 108, 2, 0.08)",
          }}
          action={
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {isViewMode && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<DifferenceIcon />}
                  onClick={() => {
                    setTabValue(0);
                    setViewVersion("diff");
                  }}
                >
                  Check Diff
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<EditIcon />}
                onClick={() => {
                  if (draftArticle?.id) {
                    navigate(`/cms/articles/${draftArticle.id}/edit`);
                  } else if (article?.latest_draft?.id) {
                    navigate(`/cms/articles/${article.latest_draft.id}/edit`);
                  } else {
                    navigate(`/cms/articles/${id}/edit`);
                  }
                }}
              >
                Edit Draft
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={async () => {
                  const draftId = draftArticle?.id || article?.latest_draft?.id;
                  if (draftId) {
                    try {
                      await articleApi.publish(draftId);
                      setNotification({
                        open: true,
                        message: "Draft published successfully!",
                        severity: "success",
                      });
                      window.location.reload();
                    } catch (err) {
                      setNotification({
                        open: true,
                        message: "Failed to publish draft",
                        severity: "error",
                      });
                    }
                  }
                }}
              >
                Publish Draft
              </Button>
            </Stack>
          }
        >
          <strong>Unpublished Draft Available:</strong> This live article has an active draft with pending changes
          {article?.latest_draft?.last_modified_by_name ? ` (Last edited by ${article.latest_draft.last_modified_by_name})` : ""}.
        </Alert>
      )}

      {article?.is_draft && originalArticle && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2,
            borderWidth: 1.5,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(2, 136, 209, 0.12)"
                : "rgba(2, 136, 209, 0.08)",
          }}
          action={
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {isViewMode && (
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  startIcon={<DifferenceIcon />}
                  onClick={() => {
                    setTabValue(0);
                    setViewVersion("diff");
                  }}
                >
                  Compare Changes (Diff)
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => navigate(`/cms/articles/${originalArticle.id}/view`)}
              >
                View Live Article
              </Button>
            </Stack>
          }
        >
          <strong>Working on Draft:</strong> This is a draft version of published article #{originalArticle.id} ("{originalArticle.title}"). Changes made here will not be live until published.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Content" />
            <Tab label="SEO" />
            <Tab label="Publishing & Sharing" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Version Selector in View Mode when draft or original exists */}
          {isViewMode && (originalArticle || draftArticle) && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Content View:
                </Typography>
                <ToggleButtonGroup
                  value={viewVersion}
                  exclusive
                  onChange={(_e, v) => v && setViewVersion(v)}
                  size="small"
                >
                  <ToggleButton value="live" disabled={!originalArticle && !article?.published}>
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                      <span>Live Published</span>
                    </Stack>
                  </ToggleButton>
                  {draftArticle && (
                    <ToggleButton value="draft">
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <EditIcon sx={{ fontSize: 16, color: "warning.main" }} />
                        <span>Draft Version</span>
                      </Stack>
                    </ToggleButton>
                  )}
                  {((originalArticle && draftArticle) || (article?.is_draft && originalArticle)) && (
                    <ToggleButton value="diff">
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <DifferenceIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        <span style={{ fontWeight: 700 }}>Changes Diff</span>
                      </Stack>
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Stack>

              {viewVersion === "diff" && (
                <Chip
                  label="Comparing Baseline vs Pending Draft"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Paper>
          )}

          {/* Render Diff directly if diff mode is selected in View Mode */}
          {isViewMode && viewVersion === "diff" && (
            <Box sx={{ mb: 3 }}>
              <MarkdownDiffViewer
                original={getBaselineMarkdown(currentLang)}
                modified={getDraftMarkdown(currentLang)}
                originalTitle={originalArticle ? `Published Live (#${originalArticle.id})` : "Published Live"}
                modifiedTitle={draftArticle ? `Draft Version (#${draftArticle.id})` : "Draft Content"}
                height={650}
              />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Article Type</InputLabel>
                <Select
                  value={article?.article_type || "post"}
                  label="Article Type"
                  onChange={(e) =>
                    setArticle((prev) => ({
                      ...prev,
                      article_type: e.target.value as Article["article_type"],
                    }))
                  }
                  disabled={isViewMode}
                >
                  <MenuItem value="post">Blog Post</MenuItem>
                  <MenuItem value="story">Story</MenuItem>
                  <MenuItem value="news">News</MenuItem>
                  <MenuItem value="shorts">Shorts</MenuItem>
                  <MenuItem value="gallery">Gallery</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={`Title (${currentLang.toUpperCase()})`}
                value={getLocalizedValue('title') || ""}
                onChange={(e) => updateLocalizedValue('title', e.target.value)}
                disabled={isViewMode}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={`Slug (${currentLang.toUpperCase()})`}
                value={getLocalizedValue('slug') || ""}
                onChange={(e) => updateLocalizedValue('slug', e.target.value)}
                error={!!article?.slug_error}
                helperText={
                  article?.slug_error ||
                  "Leave empty to auto-generate from title"
                }
                disabled={isViewMode}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`Description (${currentLang.toUpperCase()})`}
                value={getLocalizedValue('description') || ""}
                onChange={(e) => updateLocalizedValue('description', e.target.value)}
                multiline
                rows={4}
                placeholder="Enter a short description of the article"
                disabled={isViewMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={`Summary (${currentLang.toUpperCase()})`}
                value={getLocalizedValue('summary' as keyof Article) || ""}
                onChange={(e) => updateLocalizedValue('summary' as keyof Article, e.target.value)}
                multiline
                rows={3}
                placeholder="2-3 factual sentences shown at the top of the article page (not the SEO description)"
                helperText={`${(getLocalizedValue('summary' as keyof Article) || "").length} characters. Shown to readers as the quick take; leave empty to hide it.`}
                disabled={isViewMode}
              />
              {!isViewMode && (
                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  startIcon={generatingSummary ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateSummary}
                  disabled={generatingSummary || !article?.id}
                >
                  Generate summary (English and Hindi)
                </Button>
              )}
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Article Images
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Featured Image
                    </Typography>
                    <MediaSelector
                      value={article?.feature_image || 0}
                      onChange={(value: number) =>
                        handleImageSelect("feature_image", value)
                      }
                      fileTypeFilter="images"
                      previewSize="medium"
                      disabled={isViewMode}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Featured Image (Legacy)
                    </Typography>
                    <MediaSelector
                      value={article?.legacy_feature_image_url || ""}
                      onChange={(value: string) =>
                        handleImageUrlSelect("legacy_feature_image_url", value)
                      }
                      fileTypeFilter="images"
                      previewSize="medium"
                      disabled={isViewMode}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {!isViewMode && (
            <Box sx={{ mb: 3 }}>
              <ToggleButtonGroup
                value={contentType}
                exclusive
                onChange={(_event, newType) => {
                  if (newType) {
                    setContentType(newType);
                    // Initialize markdown content when switching to markdown type
                    if (newType === "markdown") {
                      const markdownKey = `content_markdown_${currentLang}` as keyof Article;
                      // @ts-ignore
                      const localizedMarkdown = article?.[markdownKey] || article?.content_markdown || "";
                      setMarkdownContent(localizedMarkdown);
                    }
                  }
                }}
                aria-label="content type"
                fullWidth
              >
                <ToggleButton value="blocks" aria-label="content blocks">
                  Content Blocks
                </ToggleButton>
                <ToggleButton value="plain" aria-label="plain text">
                  Plain Text
                </ToggleButton>
                <ToggleButton value="html" aria-label="html">
                  HTML
                </ToggleButton>
                <ToggleButton value="markdown" aria-label="markdown">
                  Markdown
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {contentType === "blocks" && (
            <EditorJSComponent
              key={currentLang} // Force remount on language change
              data={editorData}
              onChange={handleEditorDataChange}
              disabled={isViewMode}
              placeholder="Start writing your article content..."
              minHeight={500}
            />
          )}

          {contentType === "plain" && (
            <TextField
              fullWidth
              multiline
              rows={10}
              label={`Content (${currentLang.toUpperCase()})`}
              value={getLocalizedValue('content_text') || ""}
              onChange={(e) => updateLocalizedValue('content_text', e.target.value)}
              disabled={isViewMode}
            />
          )}

          {contentType === "html" && (
            <CKRichEditor
              key={currentLang}
              value={(getLocalizedValue('content_html') as string) || ""}
              onChange={(value) => updateLocalizedValue('content_html', value)}
              disabled={isViewMode}
            />
          )}

          {contentType === "markdown" && (
            <Box sx={{ mt: 2 }}>
              <MarkdownEditor
                value={
                  isViewMode
                    ? viewVersion === "live" && originalArticle
                      ? getBaselineMarkdown(currentLang)
                      : draftArticle
                      ? getDraftMarkdown(currentLang)
                      : markdownContent
                    : markdownContent
                }
                onChange={(val) => {
                  setMarkdownContent(val);
                  updateLocalizedValue('content_markdown', val);
                }}
                originalValue={getBaselineMarkdown(currentLang)}
                label={`Markdown Content (${currentLang.toUpperCase()})`}
                placeholder="Enter markdown content..."
                originalTitle={originalArticle ? `Published Live (#${originalArticle.id})` : "Published Baseline"}
                modifiedTitle={draftArticle ? `Draft Version (#${draftArticle.id})` : "Draft / Current Working Copy"}
                disabled={isViewMode}
                showDiffTab={!!originalArticle}
                height={600}
                onOpenAiAssistant={() => setDelphiAssistantOpen(true)}
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SeoEditor
            seo={(getLocalizedValue('seo') as any) || {}}
            onChange={(seo) => updateLocalizedValue('seo', seo)}
            disabled={isViewMode}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3, borderBottom: "1px solid #ddd" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Author Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Author (Public)</InputLabel>
                  <Select
                    value={selectedAuthor?.id || ""}
                    label="Author (Public)"
                    onChange={(e) => {
                      const authorId = e.target.value as number;
                      const author = authors.find(a => a.id === authorId) || null;
                      setSelectedAuthor(author);
                      setArticle((prev) => ({
                        ...prev,
                        author: author?.id || null,
                        author_name: author?.name || null,
                      }));
                    }}
                    disabled={isViewMode || loadingAuthors}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {authors.map((author) => (
                      <MenuItem key={author.id} value={author.id}>
                        {author.name} (@{author.username})
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Select the public author profile shown on the site
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Created By"
                  value={article?.created_by_name || ""}
                  disabled
                  helperText="User who originally created this article (read-only)"
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3, borderBottom: "1px solid #ddd" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={article?.is_featured || false}
                      onChange={(e) =>
                        setArticle((prev) => ({
                          ...prev,
                          is_featured: e.target.checked,
                        }))
                      }
                      disabled={isViewMode}
                    />
                  }
                  label="Featured Article"
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Publishing Settings
            </Typography>
            {article?.published ? (
              <Alert severity="success" sx={{ mb: 1, maxWidth: 600 }}>
                This article is published (Live).
              </Alert>
            ) : (
              <>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Scheduled Publish Time"
                    value={scheduledPublishDate}
                    onChange={(newValue) => {
                      setScheduledPublishDate(newValue);
                      setArticle((prev) => ({
                        ...prev,
                        scheduled_publish_at: newValue?.toISOString() || null,
                      }));
                    }}
                    minDateTime={new Date()}
                    sx={{ mb: 2 }}
                  />
                </LocalizationProvider>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={article?.auto_publish_enabled === true}
                        onChange={(e) =>
                          setArticle((prev) => ({
                            ...prev,
                            auto_publish_enabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Enable Auto-Publish"
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSchedulePublish}
                  sx={{ mt: 1 }}
                >
                  Save Publishing Schedule
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ p: 3, borderTop: "1px solid #ddd" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Social Sharing & Search Indexing
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Instant Actions:
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<TelegramIcon />}
                onClick={handleSendTelegram}
                disabled={!article?.id}
              >
                Send to Telegram Channel Now
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TelegramIcon />}
                onClick={handleSendTestTelegram}
                disabled={!article?.id || sendingTestTelegram}
              >
                {sendingTestTelegram ? "Sending Test..." : "Send Test to Telegram (@test_ai11news)"}
              </Button>
              <Button
                variant="outlined"
                color="info"
                startIcon={<TravelExploreIcon />}
                onClick={handleRequestIndexing}
                disabled={!article?.id || !article?.published}
              >
                Request Google Indexing Now
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleRequestIndexNow}
                disabled={!article?.id || !article?.published || submittingIndexNow}
              >
                {submittingIndexNow ? "Submitting to IndexNow..." : "Submit to IndexNow (Bing)"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FindInPageIcon />}
                onClick={handleCheckGoogleIndex}
                disabled={!article?.id || !article?.published || checkingGoogleIndex}
              >
                {checkingGoogleIndex ? "Checking Google Index..." : "Check Google Index Status"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleShareNow("facebook")}
                disabled={!article?.id}
              >
                Share to Facebook
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleShareNow("twitter")}
                disabled={!article?.id}
              >
                Share to Twitter
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleShareNow("instagram")}
                disabled={!article?.id}
              >
                Share to Instagram
              </Button>
            </Box>

            {article?.google_index_status && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "background.default" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TravelExploreIcon color="primary" fontSize="small" /> Google Search Console Index Status
                  </Typography>
                  {article.google_index_status.checked_at && (
                    <Typography variant="caption" color="text.secondary">
                      Last checked: {new Date(article.google_index_status.checked_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>

                {article.google_index_status.error && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {article.google_index_status.error}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {/* English Version */}
                  {article.google_index_status.english && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ p: 2, height: "100%", bgcolor: "background.paper" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            English URL
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              article.google_index_status.english.is_indexed
                                ? "INDEXED (PASS)"
                                : article.google_index_status.english.verdict === "NEUTRAL"
                                ? "NOT INDEXED (NEUTRAL)"
                                : article.google_index_status.english.verdict === "FAIL"
                                ? "FAILED"
                                : article.google_index_status.english.verdict || "UNKNOWN"
                            }
                            color={
                              article.google_index_status.english.is_indexed
                                ? "success"
                                : article.google_index_status.english.verdict === "NEUTRAL"
                                ? "warning"
                                : "error"
                            }
                            icon={
                              article.google_index_status.english.is_indexed ? (
                                <CheckCircleIcon />
                              ) : (
                                <ErrorOutlineIcon />
                              )
                            }
                          />
                        </Box>
                        <Stack spacing={0.5}>
                          {article.google_index_status.english.coverage_state && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Coverage:</strong> {article.google_index_status.english.coverage_state}
                            </Typography>
                          )}
                          {article.google_index_status.english.last_crawl_time && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Last Crawled:</strong> {new Date(article.google_index_status.english.last_crawl_time).toLocaleString()}
                            </Typography>
                          )}
                          {article.google_index_status.english.indexing_state && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Indexing State:</strong> {article.google_index_status.english.indexing_state}
                            </Typography>
                          )}
                          {article.google_index_status.english.error && (
                            <Typography variant="caption" display="block" color="error">
                              <strong>Error:</strong> {article.google_index_status.english.error}
                            </Typography>
                          )}
                          {article.google_index_status.english.url && (
                            <Typography
                              variant="caption"
                              component="a"
                              href={article.google_index_status.english.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ mt: 1, textDecoration: "underline", wordBreak: "break-all" }}
                            >
                              {article.google_index_status.english.url}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </Grid>
                  )}

                  {/* Hindi Version */}
                  {article.google_index_status.hindi && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ p: 2, height: "100%", bgcolor: "background.paper" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Hindi URL (/hi/)
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              article.google_index_status.hindi.is_indexed
                                ? "INDEXED (PASS)"
                                : article.google_index_status.hindi.verdict === "NEUTRAL"
                                ? "NOT INDEXED (NEUTRAL)"
                                : article.google_index_status.hindi.verdict === "FAIL"
                                ? "FAILED"
                                : article.google_index_status.hindi.verdict || "UNKNOWN"
                            }
                            color={
                              article.google_index_status.hindi.is_indexed
                                ? "success"
                                : article.google_index_status.hindi.verdict === "NEUTRAL"
                                ? "warning"
                                : "error"
                            }
                            icon={
                              article.google_index_status.hindi.is_indexed ? (
                                <CheckCircleIcon />
                              ) : (
                                <ErrorOutlineIcon />
                              )
                            }
                          />
                        </Box>
                        <Stack spacing={0.5}>
                          {article.google_index_status.hindi.coverage_state && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Coverage:</strong> {article.google_index_status.hindi.coverage_state}
                            </Typography>
                          )}
                          {article.google_index_status.hindi.last_crawl_time && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Last Crawled:</strong> {new Date(article.google_index_status.hindi.last_crawl_time).toLocaleString()}
                            </Typography>
                          )}
                          {article.google_index_status.hindi.indexing_state && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Indexing State:</strong> {article.google_index_status.hindi.indexing_state}
                            </Typography>
                          )}
                          {article.google_index_status.hindi.error && (
                            <Typography variant="caption" display="block" color="error">
                              <strong>Error:</strong> {article.google_index_status.hindi.error}
                            </Typography>
                          )}
                          {article.google_index_status.hindi.url && (
                            <Typography
                              variant="caption"
                              component="a"
                              href={article.google_index_status.hindi.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ mt: 1, textDecoration: "underline", wordBreak: "break-all" }}
                            >
                              {article.google_index_status.hindi.url}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {(article?.indexnow_submitted_at || (article as any)?.social_sharing?.indexnow) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "background.default" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CloudUploadIcon color="primary" fontSize="small" /> Bing / IndexNow Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {article?.indexnow_submitted_at
                      ? `Last submitted: ${new Date(article.indexnow_submitted_at).toLocaleString()}`
                      : (article as any)?.social_sharing?.indexnow?.submitted_at
                      ? `Last submitted: ${new Date((article as any).social_sharing.indexnow.submitted_at).toLocaleString()}`
                      : "Submitted"}
                  </Typography>
                </Box>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <TelegramIcon color="primary" /> Telegram Scheduled Broadcast & Teaser
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Schedule when this article should be posted to Telegram (after publication) and curate an engaging teaser message with emojis.
            </Typography>

            <Stack spacing={2} sx={{ maxWidth: 800 }}>
              {article?.social_sharing?.telegram?.status && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Telegram Status:</Typography>
                  <Chip
                    size="small"
                    label={article.social_sharing.telegram.status.toUpperCase()}
                    color={
                      article.social_sharing.telegram.status === "published"
                        ? "success"
                        : article.social_sharing.telegram.status === "failed"
                        ? "error"
                        : "warning"
                    }
                  />
                  {article.social_sharing.telegram.published_at && (
                    <Typography variant="caption" color="text.secondary">
                      (Sent on {new Date(article.social_sharing.telegram.published_at).toLocaleString()})
                    </Typography>
                  )}
                </Box>
              )}

              {article?.social_sharing?.telegram?.test_results && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">Test Send:</Typography>
                  <Chip
                    size="small"
                    label={article.social_sharing.telegram.test_results.status === "sent" ? "TEST SENT" : "TEST FAILED"}
                    color={article.social_sharing.telegram.test_results.status === "sent" ? "info" : "error"}
                  />
                  <Typography variant="caption" color="text.secondary">
                    to {article.social_sharing.telegram.test_results.channel_id || "@test_ai11news"}
                    {article.social_sharing.telegram.test_results.sent_at && ` (Sent on ${new Date(article.social_sharing.telegram.test_results.sent_at).toLocaleString()})`}
                    {article.social_sharing.telegram.test_results.error && ` (Error: ${article.social_sharing.telegram.test_results.error})`}
                  </Typography>
                </Box>
              )}

              <TextField
                label="Telegram Broadcast Date & Time"
                type="datetime-local"
                value={article?.social_sharing?.telegram?.scheduled_at ? new Date(article.social_sharing.telegram.scheduled_at).toISOString().slice(0, 16) : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  const currentStatus = article?.social_sharing?.telegram?.status;
                  setArticle((prev) => ({
                    ...prev,
                    social_sharing: {
                      ...(prev.social_sharing || {}),
                      telegram: {
                        enabled: true,
                        ...(prev.social_sharing?.telegram || {}),
                        scheduled_at: val ? new Date(val).toISOString() : null,
                        status: currentStatus === "published" ? "published" : "pending",
                      },
                    },
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ maxWidth: 300 }}
                helperText="Leave blank to broadcast as soon as article is published"
              />

              <TextField
                label="Curated Telegram Teaser / Message"
                multiline
                rows={4}
                fullWidth
                value={article?.social_sharing?.telegram?.message || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setArticle((prev) => ({
                    ...prev,
                    social_sharing: {
                      ...(prev.social_sharing || {}),
                      telegram: {
                        enabled: true,
                        ...(prev.social_sharing?.telegram || {}),
                        message: val,
                      },
                    },
                  }));
                }}
                placeholder="Engaging emoji-rich teaser crafted specifically for Telegram channel. Leave blank to fallback to standard bilingual summary."
                helperText="If provided, this curated teaser will be sent instead of the default bilingual website summary."
              />
            </Stack>

            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSocialSharing}
                disabled={!article?.id}
              >
                Save Social Sharing Settings
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TelegramIcon />}
                onClick={handleSendTestTelegram}
                disabled={!article?.id || sendingTestTelegram}
              >
                {sendingTestTelegram ? "Sending Test..." : "Send Test Message (@test_ai11news)"}
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      <DelphiAiAssistant
        open={delphiAssistantOpen}
        onClose={() => setDelphiAssistantOpen(false)}
        article={article}
        currentMarkdown={
          currentLang === 'en'
            ? markdownContent || article.content_markdown_en || article.content_markdown || ""
            : article.content_markdown_en || article.content_markdown || markdownContent || ""
        }
        onApplyContent={handleApplyDelphiContent}
        onApplyHindi={handleApplyDelphiHindi}
        onApplyImage={handleApplyDelphiImage}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default ArticleEditor;
